import { Banner, BlockStack, Button, Text } from '@shopify/polaris';
import { useEffect, useRef, useState } from 'react';
import type { StoreInventoryProgress, StoreRemovalPreview } from '../../../../packages/shared/shopify-privacy/store-review';
import { useApi } from '../../hooks/useApi';
import { TextField } from '../forms';

type Props = { eventId:string; review:StoreInventoryProgress; onReview:(review:StoreInventoryProgress|null)=>void; onApplied:()=>Promise<void> };
type Saved = {review:StoreInventoryProgress|null};

export default function StoreRemovalApproval({eventId,review,onReview,onApplied}: Props) {
  const {fetchApi,loading,error} = useApi();
  const [preview,setPreview] = useState<StoreRemovalPreview|null>(null);
  const [domain,setDomain] = useState('');
  const [running,setRunning] = useState(false);
  const run = useRef(0);
  useEffect(() => () => { run.current++; },[]);
  const path = `/api/shopify-privacy/${encodeURIComponent(eventId)}`;
  const list = {review_id:review.id,review_revision:review.revision};
  const post = (data:Record<string,unknown>) => ({method:'POST',body:JSON.stringify(data)});
  const decide = async () => {
    const result = await fetchApi<{preview:StoreRemovalPreview}>(path+'/store-removal-preview',post(list));
    if (result) { setPreview(result.preview); setDomain(''); }
  };
  const prepare = async (saved:StoreInventoryProgress) => {
    const current = ++run.current;
    setRunning(true);
    let decision = saved.decision;
    while (decision?.stage === 'notices' && current === run.current) {
      const prior = decision.revision;
      const result = await fetchApi<Saved>(path+'/store-removal-prepare',post({review_id:saved.id,decision_revision:prior}));
      if (current !== run.current || !result?.review) break;
      onReview(result.review);
      decision = result.review.decision;
      if (decision?.revision === prior) break;
    }
    if (current === run.current) setRunning(false);
  };
  const approve = async () => {
    if (!preview || domain !== preview.domain) return;
    const result = await fetchApi<Saved>(path+'/store-removal-approval',post({...list,confirm_domain:domain,token:preview.token}));
    if (result?.review) { setPreview(null); onReview(result.review); await prepare(result.review); }
  };
  const reload = async () => {
    const result = await fetchApi<Saved>(path+'/store-inventory');
    if (result) { setPreview(null); setDomain(''); onReview(result.review); if (!result.review) await onApplied(); }
  };
  const apply = async () => {
    const result = await fetchApi<{removal:{removal_id:string}}>(path+'/store-removal-apply',
      post({review_id:review.id,decision_revision:review.decision?.revision}));
    if (result?.removal) await onApplied();
  };
  return <BlockStack gap="200">
    {error && <><Banner tone="critical">{error}</Banner><Button onClick={reload} disabled={loading}>Reload store review</Button></>}
    {review.decision ? <>
      <Text as="p">Removal approved. Prepared {review.decision.notices_prepared} of {review.decision.notices_total} Shopify notices. The store request is open.</Text>
      {review.decision.stage === 'notices' && (running
        ? <Button onClick={() => { run.current++; setRunning(false); }}>Pause notice preparation</Button>
        : <Button onClick={() => void prepare(review)} disabled={loading}>Continue notice preparation</Button>)}
      {review.decision.stage === 'prepared' && <Button tone="critical" disabled={loading} onClick={apply}>Remove approved store data</Button>}
    </> : preview ? <>
      <Text as="p">Removal for {preview.domain} will permanently clear personal details from {preview.orders} orders, remove {preview.customers} customer records and queue {preview.private_files} private files for deletion. Warehouse quantities stay on record.</Text>
      <TextField label="Type the full Shopify store domain to approve removal" value={domain} onChange={setDomain}
        autoComplete="off" disabled={loading} helpText="This approval form expires in five minutes." />
      <Button tone="critical" onClick={approve} disabled={loading || domain !== preview.domain}>Approve store removal</Button>
      <Button variant="plain" onClick={() => { setPreview(null); setDomain(''); }}>Cancel</Button>
    </> : <Button onClick={decide} disabled={loading}>Review removal decision</Button>}
  </BlockStack>;
}
